/* eslint-disable @typescript-eslint/naming-convention */
import {baseApi} from 'core/redux/store/api';
import {IFilterFacetResponse} from 'plugins/modules/filter/types';
import {TGetProductsResponse} from 'modules/products/models';
import {ICatalogSEO, IProductShort} from 'modules/products/types';
import {createListingApiService, TGetListingRequest} from 'plugins/modules/listing/api/api';
import {TGetListingTransformedResponse} from 'plugins/modules/listing/types';
import {buildHeaders} from 'core/helpers/api/headers';
import {API_VERSION} from 'core/helpers/env';
import {IPagination} from 'core/models';
import {IStock} from 'modules/discount/types';

export interface IGetCatalogFiltersProps {
    filter?: string;
}

export interface IGetCatalogTransformedResponse {
    list: TGetProductsResponse['data']['items'];
    pagination: TGetProductsResponse['pagination'];
    errors?: TGetProductsResponse['errors'];
    seo?: ICatalogSEO;
    meta?: TGetProductsResponse['meta'];
}

const API_PATHS = {
    DISCOUNT_PRODUCTS: `${API_VERSION}/discount`,
    DISCOUNT_FACET_SEARCH: `${API_VERSION}/discount/facetsearch`,
    STOCK_LIST: `${API_VERSION}/stocks`,
} as const;

export const discountApiService = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getDiscountFilters: builder.query({
            providesTags: ['FacetFilters'],
            query: ({filter}: IGetCatalogFiltersProps) => {
                const baseUrl = API_PATHS.DISCOUNT_FACET_SEARCH;
                return filter ? `${baseUrl}?${filter}` : baseUrl;
            },
            transformResponse: (response: {data: IFilterFacetResponse}) => response?.data,
        }),
    }),
});

const discountProductsListingApiService = createListingApiService<
    IProductShort,
    TGetProductsResponse,
    TGetListingTransformedResponse<IProductShort>,
    TGetListingRequest
>({
    name: 'getDiscount',
    query: (query) => {
        const headers = buildHeaders({withSeo: 'detail'});
        const sort = query.sort ? `&sort=${query.sort}` : '';

        return {
            url: `${API_PATHS.DISCOUNT_PRODUCTS}?${query.filter}&pageNumber=${query.pageNumber}&pageSize=${
                query.pageSize
            }${sort}&withMeta=${query.withMeta ? 1 : 0}`,
            headers,
        };
    },
    serializeQueryArgs: ({endpointName, queryArgs}) => {
        return `${endpointName}${queryArgs.filter ? `_${queryArgs.filter}` : ''}_${queryArgs.sort}`;
    },
    forceRefetchValuesKeys: [],
});

export const {getDiscount} = discountProductsListingApiService.endpoints;
export const {useLazyGetDiscountQuery} = discountProductsListingApiService;

export const {useGetDiscountFiltersQuery} = discountApiService;

interface IGetStockListResponse {
    data: IGetStockListData;
    pagination: IPagination;
}

interface IGetStockListData {
    items: IStock[];
}

export const StockListApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getStockList: builder.query<
            IGetStockListData['items'],
            {withProducts?: boolean; withoutCode?: IStock['code']} & Partial<Pick<IPagination, 'pageSize'>>
        >({
            query: ({withProducts = true, pageSize = 100, withoutCode}) => {
                return `${API_PATHS.STOCK_LIST}${pageSize ? `?pageSize=${pageSize}` : ''}${withProducts ? `&withProducts=true` : ''}${withoutCode ? `&withoutCode=${withoutCode}` : ''}`;
            },
            transformResponse: (response: IGetStockListResponse) => response?.data.items,
        }),
    }),
});

export const {getStockList} = StockListApi.endpoints;
export const {useGetStockListQuery} = StockListApi;
